import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {Observable, ReplaySubject, of, BehaviorSubject} from 'rxjs';
import {shareReplay, tap, catchError} from 'rxjs/operators';
import {StateStorageService} from '@supoui/core/auth/state-storage.service';
import {SessionStorageService} from 'ngx-webstorage';
import {NGXLogger} from 'ngx-logger';

import {SERVER_API_URL} from '../../app.constants';
import {Account} from '@supoui/core/user/account.model';
import {TranslateService} from '@ngx-translate/core';
import {PasswordChange} from "@supoui/core/user/password-change.model";

@Injectable({providedIn: 'root'})
export class AccountService {
  private userIdentity: Account | null = null;
  private authenticationState = new ReplaySubject<Account | null>(1);
  private accountCache$?: Observable<Account | null>;

  private _languageChanged = new BehaviorSubject<string>('');
  public readonly languageChanged$ = this._languageChanged.asObservable();

  constructor(
    private translateService: TranslateService,
    private sessionStorage: SessionStorageService,
    private http: HttpClient,
    private stateStorageService: StateStorageService,
    private router: Router,
    private logger: NGXLogger
  ) {
  }

  save(account: Account): Observable<{}> {
    return this.http.post(SERVER_API_URL + 'services/supo-user-administration/api/account', account);
  }

  languageChanged(languageKey: string): void {
    this._languageChanged.next(languageKey);
  }

  authenticate(identity: Account | null): void {
    this.userIdentity = identity;
    this.authenticationState.next(this.userIdentity);
  }

  hasAnyAuthority(authorities: string[] | string): boolean {
    if (!this.userIdentity || !this.userIdentity.authorities) {
      return false;
    }
    if (!Array.isArray(authorities)) {
      authorities = [authorities];
    }
    return this.userIdentity.authorities.some((authority: string) => authorities.includes(authority));
  }

  identity(force?: boolean): Observable<Account | null> {
    if (!this.accountCache$ || force || !this.isAuthenticated()) {
      this.accountCache$ = this.fetch().pipe(
        catchError(() => {
          return of(null);
        }),
        tap((account: Account | null) => {
          this.authenticate(account);

          // After retrieve the account info, the language will be changed to
          // the user's preferred language configured in the account setting
          if (account && account.langKey) {
            const langKey = this.sessionStorage.retrieve('locale') || account.langKey;
            this.translateService.use(langKey);
          }

          if (account) {
            this.navigateToStoredUrl();
          }
        }),
        shareReplay()
      );
    }
    return this.accountCache$;
  }

  isAuthenticated(): boolean {
    return this.userIdentity !== null;
  }

  getAuthenticationState(): Observable<Account | null> {
    return this.authenticationState.asObservable();
  }

  getLogin(): string {
    return this.userIdentity ? this.userIdentity.login : '';
  }

  getImageUrl(): string {
    return this.userIdentity ? this.userIdentity.imageUrl : '';
  }

  getSupplierProfiles(): string[] {
    return this.userIdentity ? this.userIdentity.supplierProfiles : [];
  }

  getSupplierRoles(): string[] {
    return this.userIdentity ? this.userIdentity.supplierRoles : [];
  }

  isAdmin(): boolean {
    return !!this.getSupplierRoles().find(role => role === 'ADMIN');
  }

  private fetch(): Observable<Account> {
    return this.http.get<Account>(SERVER_API_URL + 'services/supo-user-administration/api/account');
  }

  private navigateToStoredUrl(): void {
    // previousState can be set in the authExpiredInterceptor and in the userRouteAccessService
    // if login is successful, go to stored previousState and clear previousState
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl);
    }
  }

  resetPassword(login: string): Observable<void> {
    return this.http.post<void>(SERVER_API_URL + 'services/supo-user-administration/api/account/reset-password/init', login);
  }

  changePassword(passwordChange: PasswordChange): Observable<void> {
    return this.http.post<void>(SERVER_API_URL + 'services/supo-user-administration/api/account/change-password', passwordChange);
  }

}
